import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { ActionButton } from "@via/components/action-button/action-button";
import Drawer from "@via/components/drawer";
import { GenericError } from "@via/components/error";
import Table, { TableSize } from "@via/components/table/table";
import { BUTTONS, ERROR } from "@via/utils/constants";
import { formatDateString } from "@via/utils/helpers";
import { useEffect, useState } from "react";
import {
  usePersonalAccessTokens,
  useDeletePersonalAccessToken,
} from "@via/pages/account/profile/personal-access-tokens/personal-access-tokens.api";
import useUrlParams from "@via/hooks/useURLParams";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import CreatePersonalAccessTokenForm from "@via/pages/account/profile/personal-access-tokens/components/create-personal-access-token-form";
import TokenDisplayModal from "@via/pages/account/profile/personal-access-tokens/components/token-display-modal";
import TableContextMenu from "@via/components/table/table-context-menu";
import { TableContextMenuInterface } from "@via/utils/interfaces";
import { Badge } from "@via/components/badge";
import { SparklesIcon } from "@heroicons/react/24/outline";
import AccessScopeBadge from "@via/pages/account/profile/personal-access-tokens/components/access-scope-badge";
import {
  PersonalAccessToken,
  PersonalAccessTokenScope,
  PersonalAccessTokenUsage,
} from "common";

const columnHelper = createColumnHelper<PersonalAccessToken>();

const defaultParams = {
  sort: [{ id: "createdAt", desc: true }],
  page: 0,
  pageSize: 10,
};

const PersonalAccessTokens = () => {
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();

  const [tokenToBeDeleted, setTokenToBeDeleted] =
    useState<PersonalAccessToken | null>(null);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [newToken, setNewToken] = useState<{
    token: string;
    expiresAt: string;
    usageType: PersonalAccessTokenUsage;
    scope: PersonalAccessTokenScope | null;
  } | null>(null);
  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );

  const { data, isLoading, isError } = usePersonalAccessTokens(
    page,
    pageSize,
    sort
  );

  const {
    mutateAsync: deletePersonalAccessToken,
    isPending: isDeletePersonalAccessTokenLoading,
  } = useDeletePersonalAccessToken();

  useEffect(() => {
    setUrlParams({
      sort,
      page,
      pageSize,
    });
  }, [sort, page, pageSize]);

  const handleAction = (
    option: TableContextMenuInterface,
    row: PersonalAccessToken
  ): void => {
    switch (option.name) {
      case "Delete":
        setTokenToBeDeleted(row);
        break;
      default:
        break;
    }
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: "Name",
      cell: info => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{info.getValue()}</span>
          {isExpired(info.row.original.expiresAt) && (
            <Badge
              text="Expired"
              textColor="text-red-600"
              fillColor="fill-red-600"
            />
          )}
        </div>
      ),
      enableSorting: true,
    }),
    columnHelper.accessor("usageType", {
      id: "usageType",
      header: "Usage",
      cell: info => {
        const usageType = info.getValue();
        const isMcp = usageType === PersonalAccessTokenUsage.MCP;
        return isMcp ? (
          <span className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium bg-gradient-to-r from-purple-50 to-blue-50 text-purple-700 ring-1 ring-inset ring-purple-200">
            <SparklesIcon className="h-3.5 w-3.5 text-purple-600" />
            MCP
          </span>
        ) : (
          <Badge
            text="General"
            textColor="text-gray-600"
            fillColor="fill-gray-400"
          />
        );
      },
      enableSorting: false,
    }),
    columnHelper.accessor("scope", {
      id: "scope",
      header: "Access",
      // A row from a server older than access levels has no scope field: it is a full access token.
      cell: info => <AccessScopeBadge scope={info.getValue() ?? null} />,
      enableSorting: false,
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: "Created",
      cell: info => formatDateString(info.getValue()),
      enableSorting: true,
    }),
    columnHelper.accessor("expiresAt", {
      id: "expiresAt",
      header: "Expires",
      cell: info => {
        const date = formatDateString(info.getValue());
        const expired = isExpired(info.getValue());
        return (
          <span className={expired ? "text-red-600 font-medium" : ""}>
            {date}
          </span>
        );
      },
      enableSorting: true,
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <TableContextMenu
          options={[
            {
              name: "Delete",
              href: "#",
            },
          ]}
          onSelect={option => handleAction(option, row.original)}
        />
      ),
      enableSorting: false,
    }),
  ] as Array<ColumnDef<PersonalAccessToken>>;

  const handleTokenCreated = (
    token: string,
    expiresAt: string,
    usageType: PersonalAccessTokenUsage,
    scope: PersonalAccessTokenScope | null
  ) => {
    setNewToken({ token, expiresAt, usageType, scope });
  };

  return (
    <div className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
      <div className="via-page-heading-wrapper">
        <h2 className="via-h2">Personal Access Tokens</h2>
        <ActionButton
          button={BUTTONS.CREATE_TOKEN}
          onClick={() => {
            setIsCreateDrawerOpen(true);
          }}
        />
      </div>

      {isError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <Table
          isLoading={isLoading}
          data={data?.data}
          columns={columns}
          sort={sort}
          setSort={setSort}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={data?.total}
          size={TableSize.Big}
        />
      )}
      <DeleteModal
        item={tokenToBeDeleted}
        setItem={setTokenToBeDeleted}
        deleteFunction={() => deletePersonalAccessToken(tokenToBeDeleted!.id)}
        isLoading={isDeletePersonalAccessTokenLoading}
        title="Delete Personal Access Token"
        message={name => (
          <>
            Are you sure you want to delete the token{" "}
            <strong className="font-semibold">{name}</strong>? Applications
            using this token will no longer be able to access the Foundry
            Connect API. This action cannot be undone.
          </>
        )}
      />
      <Drawer
        id="create-personal-access-token"
        open={isCreateDrawerOpen}
        setOpen={setIsCreateDrawerOpen}>
        <CreatePersonalAccessTokenForm
          setOpen={setIsCreateDrawerOpen}
          onSuccess={handleTokenCreated}
        />
      </Drawer>
      {newToken && (
        <TokenDisplayModal
          token={newToken.token}
          expiresAt={newToken.expiresAt}
          usageType={newToken.usageType}
          scope={newToken.scope}
          onClose={() => setNewToken(null)}
        />
      )}
    </div>
  );
};

export default PersonalAccessTokens;
